import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import theme from "@/theme";
import { convertPxToRem, mapErrorToMessage } from "@/utils";
import { InputLabel, Link, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ScrollCards } from "../../../Common/components/ScrollCards";
import VoucherInputField from "../VoucherInputField";
import { trackEvent, trackUserInteraction } from "@/services/analytics-adapter";
import FullScreenIframeDialog from "@/features/Common/components/FullScreenIframeDialog";

interface SupportPanelProps {
  onGetSupportClick: () => void;
}

function SupportPanel({ onGetSupportClick }: SupportPanelProps) {
  const { t } = useTranslation();

  return (
    <Stack
      spacing={2}
      sx={{
        backgroundColor: theme.palette.primary[100],
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Stack flexDirection="row" gap={1} alignItems="center">
        <IconLoader
          sx={{
            width: convertPxToRem(24),
            height: convertPxToRem(24),
          }}
          icon="PadlockIcon"
        />
        <Typography variant="h4" sx={{ fontSize: 12 }}>
          {t("AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.title")}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h4" fontWeight="400" sx={{ fontSize: 12 }} pt={1}>
          {t("AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.description")}
        </Typography>
      </Stack>

      <ButtonWithAnalytics
        page="PathologyPanel"
        text={t(
          "AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.buttonText"
        )}
        intent="navigational"
        variant="outlined"
        color="neutral"
        size="small"
        onClick={onGetSupportClick}
      >
        {t("AddOrUseVoucher.Voucher.UseVoucherTab.supportPanel.buttonText")}
      </ButtonWithAnalytics>
    </Stack>
  );
}

interface UseVoucherTabProps {
  onGetSupportClick: () => void;
  onApplyVoucherClick: (voucherCode: string) => void;
  applyVoucherError: any;
  voucherCode: string | undefined;
}

export default function UseVoucherTab({
  onGetSupportClick,
  onApplyVoucherClick,
  applyVoucherError,
  voucherCode: voucherCodeFromContext,
}: UseVoucherTabProps) {
  const { t } = useTranslation();

  const [voucherCode, setVoucherCode] = useState(voucherCodeFromContext || "");
  const errorMessage = mapErrorToMessage(applyVoucherError, t);
  const [hasError, setHasError] = useState(Boolean(applyVoucherError));
  const [isTermsAndConditionsDialogOpen, setIsTermsAndConditionsDialogOpen] =
    useState(false);

  const handleVoucherCodeChange = (code: string) => {
    setVoucherCode(code);
    if (applyVoucherError) {
      setHasError(false);
    }
  };

  const handleApplyVoucherClick = () => {
    onApplyVoucherClick(voucherCode);
  };

  const termsAndConditionsDocumentTitle = "Voucher T&Cs";
  const termsAndConditionsDocumentUrl =
    "https://d1cqzwpyvsxlnx.cloudfront.net/contracts/vouchering-terms-and-conditions.html";

  function onTermsDialogClose() {
    setIsTermsAndConditionsDialogOpen(false);
  }

  function onTermsLinkClick(event: any) {
    event.preventDefault();
    setIsTermsAndConditionsDialogOpen(true);
    trackEvent({
      event: "action.termsAndConditionsLinkOpened",
      source: "Use a voucher",
    });
    trackUserInteraction({
      linkText: `Use a voucher disclaimer | Terms and Conditions`,
      linkIntent: "navigational",
      linkScope: "button",
    });
  }

  const useVoucherCardData = [
    {
      title: t("AddOrUseVoucher.Voucher.UseVoucherTab.cardOne.title"),
      description: t(
        "AddOrUseVoucher.Voucher.UseVoucherTab.cardOne.description"
      ),
      iconName: "VoucherUseIcon",
    },
    {
      title: t("AddOrUseVoucher.Voucher.UseVoucherTab.cardTwo.title"),
      description: t(
        "AddOrUseVoucher.Voucher.UseVoucherTab.cardTwo.description"
      ),
      iconName: "VoucherReceiveIcon",
    },
    {
      title: t("AddOrUseVoucher.Voucher.UseVoucherTab.cardThree.title"),
      description: t(
        "AddOrUseVoucher.Voucher.UseVoucherTab.cardThree.description"
      ),
      iconName: "VoucherApplyIcon",
    },
  ];

  return (
    <>
      <Stack spacing={3}>
        <Typography variant="h4" fontWeight={600}>
          {t("AddOrUseVoucher.Voucher.UseVoucherPage.title")}
        </Typography>
        <ScrollCards cardsData={useVoucherCardData} />
        <Panel>
          <Stack
            spacing={1}
            component="button"
            sx={{
              cursor: "pointer",
              border: "none",
              background: "transparent",
              textAlign: "left",
            }}
          >
            <Typography variant="h4">
              {t("AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.title")}
            </Typography>
            <Typography variant="body2" color="neutral.light">
              <Trans i18nKey="AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.description">
                <Link
                  sx={{ color: "primary", fontWeight: 900 }}
                  href="/"
                  onClick={onTermsLinkClick}
                ></Link>
              </Trans>
            </Typography>
            <Stack sx={{ width: "100%" }}>
              <InputLabel
                htmlFor={t(
                  "AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.label"
                )}
              >
                <Typography
                  textAlign="left"
                  variant="body2"
                  color={hasError ? "error" : "neutral.main"}
                >
                  {t("AddOrUseVoucher.Voucher.UseVoucherTab.codeInput.label")}
                </Typography>
              </InputLabel>
              <VoucherInputField
                onVoucherCodeChange={handleVoucherCodeChange}
                errorMessage={errorMessage}
                voucherCode={voucherCode}
              />
            </Stack>
          </Stack>
        </Panel>
        <SupportPanel onGetSupportClick={onGetSupportClick} />
        <ButtonWithAnalytics
          page="Use Voucher"
          text={t("AddOrUseVoucher.Voucher.UseVoucherTab.submitButton")}
          intent="navigational"
          disabled={hasError || !voucherCode}
          onClick={handleApplyVoucherClick}
        >
          {t("AddOrUseVoucher.Voucher.UseVoucherTab.submitButton")}
        </ButtonWithAnalytics>
      </Stack>
      <FullScreenIframeDialog
        title={termsAndConditionsDocumentTitle}
        isOpen={isTermsAndConditionsDialogOpen}
        onClose={onTermsDialogClose}
        url={termsAndConditionsDocumentUrl}
      />
    </>
  );
}
